/*
  This context provider shares all of the information about the current blog post, so that we can access stuff within our interactive components.

  It also manages the hit counter, fetching and incrementing the value. We need to do it up here since some blog posts render the hit counter in multiple places.
*/
'use client';

import * as React from 'react';

import type { ContentData } from '@/types/content.types';

import { useBlogPostMetrics } from './ContentProvider.helpers';
import { ContentContextValues } from './ContentProvider.types';

interface Props {
  contentData: ContentData;
  children: React.ReactNode;
}

export const ContentContext =
  React.createContext<ContentContextValues>({
    // @ts-ignore
    contentData: null,
    metrics: {
      hits: null,
      totalNumLikes: null,
      totalNumLikesFromUser: null,
      lastInteractionAt: 0,
    },
    actions: {
      toggleLikeButton: () => 0,
    },
  });

function ContentProvider({ contentData, children }: Props) {
  const { metrics, actions } = useBlogPostMetrics(contentData);

  const data = React.useMemo(
    () => ({
      contentData,
      metrics,
      actions,
    }),
    [contentData, metrics, actions]
  );

  return (
    <ContentContext.Provider value={data}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = React.useContext(ContentContext);

  if (context === undefined) {
    throw new Error(
      'useContent must be used within a ContentProvider'
    );
  }

  return context;
}

export default ContentProvider;
